import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Truck,
  MapPin,
  Phone,
  User,
  Clock,
  CheckCircle2,
  Search,
  Calendar,
  FileText,
  Printer,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  ExternalLink,
  RefreshCw,
  X,
  Package,
  AlertCircle,
  Maximize2,
} from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import Receipt from '@/components/pos/Receipt';
import DeliveryMapModal from '@/components/deliveries/DeliveryMapModal';
import DeliveryLiveMap from '@/components/deliveries/DeliveryLiveMap';
import { useDeliveryPersonLocationTracker } from '@/hooks/useDeliveryPersonLocationTracker';
import { deliveriesApi } from '@/services/api';
import { formatMoney, formatDate } from '@/utils/currency';
import { ROLES, type DeliveryOrder } from '@/types';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';

interface TeamMember {
  id: string;
  name: string;
  role: string;
}

function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function DeliveriesPage() {
  const qc = useQueryClient();
  const { role } = useAuthStore();
  const isDeliveryPerson = role === ROLES.DELIVERY_PERSON;

  const [activeTab, setActiveTab] = useState<'Active' | 'Pending' | 'Out for Delivery' | 'Delivered' | 'All'>('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState<string>(() => getTodayDateString());
  const [toDate, setToDate] = useState<string>(() => getTodayDateString());
  const [page, setPage] = useState(1);
  const [selectedDriverFilter, setSelectedDriverFilter] = useState('');

  // Admin Live Map & View state
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapInitialStatus, setMapInitialStatus] = useState<'All' | 'Active' | 'Out for Delivery' | 'Pending' | 'Delivered'>('All');
  const [outForDeliveryView, setOutForDeliveryView] = useState<'list' | 'map'>('list');

  // Location tracking for delivery persons (saves current location only)
  const locationTracker = useDeliveryPersonLocationTracker();

  // Item packing checklist state for drivers
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const toggleItemCheck = (orderName: string, idx: number) => {
    const key = `${orderName}-${idx}`;
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Expanded items state
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});

  // Receipt Modal
  const [receiptInvoiceName, setReceiptInvoiceName] = useState<string | null>(null);

  // Status Update Modal
  const [updatingOrder, setUpdatingOrder] = useState<DeliveryOrder | null>(null);
  const [targetStatus, setTargetStatus] = useState<string>('Delivered');
  const [isInternalTeam, setIsInternalTeam] = useState<boolean>(true);
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>('');
  const [externalDriverDetails, setExternalDriverDetails] = useState<string>('');
  const [statusNotes, setStatusNotes] = useState('');

  // Fetch Team Members for driver assignment
  const { data: teamMembers = [] } = useQuery<TeamMember[]>({
    queryKey: ['deliveryTeamMembers'],
    queryFn: async () => {
      const res = await deliveriesApi.getTeamMembers();
      return res.data.message || [];
    },
    staleTime: 60_000,
    enabled: !isDeliveryPerson,
  });

  // Only show staff with the Delivery Person role
  const deliveryPersons = useMemo(() => {
    return teamMembers.filter((m) => !m.role || m.role === 'Delivery Person');
  }, [teamMembers]);

  // Fetch Deliveries
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['deliveries', activeTab, searchTerm, fromDate, toDate, page, selectedDriverFilter, isDeliveryPerson],
    queryFn: async () => {
      const res = await deliveriesApi.getList({
        status: activeTab,
        search: searchTerm,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        page,
        limit: 25,
        driver: isDeliveryPerson ? undefined : (selectedDriverFilter || undefined),
      });
      return res.data.message || { items: [], total: 0, summary: {} };
    },
    staleTime: 10_000,
  });

  const orders: DeliveryOrder[] = data?.items || [];
  const summary = data?.summary || { pending: 0, out_for_delivery: 0, delivered: 0, active: 0 };
  const totalPages = data?.total_pages || 1;

  // Mutation for updating delivery status
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      invoiceName,
      status,
      notes,
      driver,
    }: {
      invoiceName: string;
      status: string;
      notes?: string;
      driver?: string;
    }) => {
      return (await deliveriesApi.updateStatus(invoiceName, status, notes, driver)).data;
    },
    onSuccess: (_, variables) => {
      toast.success(`Delivery status updated to ${variables.status}`);
      qc.invalidateQueries({ queryKey: ['deliveries'] });
      setUpdatingOrder(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to update delivery');
    },
  });

  const handleOpenStatusModal = (order: DeliveryOrder, defaultNextStatus?: string) => {
    setUpdatingOrder(order);
    setTargetStatus(defaultNextStatus || (order.delivery_status === 'Pending' ? 'Out for Delivery' : 'Delivered'));
    setStatusNotes('');

    const existingDriver = (order.delivery_driver || '').trim();
    if (existingDriver) {
      const isInternalMatch = deliveryPersons.some(
        (m) => m.name.toLowerCase() === existingDriver.toLowerCase()
      );
      if (isInternalMatch) {
        setIsInternalTeam(true);
        setSelectedTeamMember(existingDriver);
        setExternalDriverDetails('');
      } else {
        setIsInternalTeam(false);
        setSelectedTeamMember('');
        setExternalDriverDetails(existingDriver);
      }
    } else {
      setIsInternalTeam(true);
      setSelectedTeamMember('');
      setExternalDriverDetails('');
    }
  };

  const handleSaveStatusUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatingOrder) return;
    const finalDriver = isDeliveryPerson
      ? undefined
      : isInternalTeam
      ? selectedTeamMember.trim()
      : externalDriverDetails.trim();

    updateStatusMutation.mutate({
      invoiceName: updatingOrder.name,
      status: targetStatus,
      notes: statusNotes,
      driver: finalDriver,
    });
  };

  const toggleExpand = (orderName: string) => {
    setExpandedOrders((prev) => ({ ...prev, [orderName]: !prev[orderName] }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3 text-amber-500" />
            Pending Delivery
          </span>
        );
      case 'Out for Delivery':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <Truck className="w-3 h-3 text-blue-500" />
            Out for Delivery
          </span>
        );
      case 'Delivered':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Delivered
          </span>
        );
      case 'Cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
            <X className="w-3 h-3 text-red-500" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            {status}
          </span>
        );
    }
  };

  const getPaymentBadge = (status: string, balance: number) => {
    if (status === 'Paid' || balance <= 0) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800">
          Paid
        </span>
      );
    }
    if (status === 'Partially Paid') {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-800">
          Partial ({formatMoney(balance)} due)
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-red-100 text-red-800">
        Unpaid ({formatMoney(balance)})
      </span>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      <Sidebar />

      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200/80 px-6 py-4 flex flex-wrap items-center justify-between gap-4 sticky top-0 z-10 shadow-2xs">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                {isDeliveryPerson ? 'My Deliveries' : 'Deliveries'}
              </h1>
              <p className="text-xs text-slate-500">
                {isDeliveryPerson
                  ? 'Your assigned deliveries, navigation addresses, and status updates'
                  : 'Manage dispatch, track in-transit orders, and confirm deliveries'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Delivery Person GPS Status */}
            {isDeliveryPerson && (
              <div className="flex items-center gap-2">
                {locationTracker.isTracking ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Live Location Active</span>
                    {locationTracker.lastUpdated && (
                      <span className="text-[10px] text-emerald-600/70 font-normal">
                        ({locationTracker.lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={locationTracker.startTracking}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition-colors"
                    title="Enable location tracking so admin can view your delivery location"
                  >
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                    <span>{locationTracker.error ? 'GPS Needed: Enable' : 'Enable Location'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Admin Live Map Button */}
            {!isDeliveryPerson && (
              <button
                type="button"
                onClick={() => {
                  setMapInitialStatus(activeTab === 'All' ? 'All' : activeTab);
                  setShowMapModal(true);
                }}
                className="flex items-center gap-2 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:shadow"
                title="View delivery persons live map"
              >
                <MapPin className="w-4 h-4" />
                <span>Live Map</span>
                {summary.out_for_delivery > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full font-semibold">
                    {summary.out_for_delivery} in transit
                  </span>
                )}
              </button>
            )}

            <button
              type="button"
              onClick={() => refetch()}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
              title="Refresh deliveries"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-blue-600' : ''}`} />
            </button>
          </div>
        </header>

        {/* Content Container */}
        <div className="p-6 space-y-6 max-w-7xl w-full mx-auto">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Active Deliveries */}
            <div
              onClick={() => setActiveTab('Active')}
              className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                activeTab === 'Active'
                  ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-500/20'
                  : 'bg-white border-slate-200/80 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {isDeliveryPerson ? 'My Assigned' : 'To Deliver'}
                </span>
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                  <Truck className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-slate-900 mt-2">{summary.active}</p>
              <p className="text-xs text-slate-500 mt-1">
                {isDeliveryPerson ? 'Pending & in-transit' : 'Pending & Out for Delivery'}
              </p>
            </div>

            {/* Pending Dispatch */}
            <div
              onClick={() => setActiveTab('Pending')}
              className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                activeTab === 'Pending'
                  ? 'bg-amber-50/70 border-amber-300 ring-2 ring-amber-500/20'
                  : 'bg-white border-slate-200/80 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {isDeliveryPerson ? 'To Pick Up' : 'Pending'}
                </span>
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-amber-700 mt-2">{summary.pending}</p>
              <p className="text-xs text-slate-500 mt-1">
                {isDeliveryPerson ? 'Orders waiting for pick up' : 'Orders waiting to be dispatched'}
              </p>
            </div>

            {/* Out for Delivery */}
            <div
              onClick={() => setActiveTab('Out for Delivery')}
              className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                activeTab === 'Out for Delivery'
                  ? 'bg-indigo-50/70 border-indigo-300 ring-2 ring-indigo-500/20'
                  : 'bg-white border-slate-200/80 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">In Transit</span>
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <Truck className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-indigo-700 mt-2">{summary.out_for_delivery}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-slate-500">
                  {isDeliveryPerson ? 'Currently out with you' : 'Out with driver / rider'}
                </p>
                {!isDeliveryPerson && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapInitialStatus('Out for Delivery');
                      setShowMapModal(true);
                    }}
                    className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                  >
                    Map →
                  </span>
                )}
              </div>
            </div>

            {/* Delivered */}
            <div
              onClick={() => setActiveTab('Delivered')}
              className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                activeTab === 'Delivered'
                  ? 'bg-emerald-50/70 border-emerald-300 ring-2 ring-emerald-500/20'
                  : 'bg-white border-slate-200/80 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivered</span>
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-emerald-700 mt-2">{summary.delivered}</p>
              <p className="text-xs text-slate-500 mt-1">
                {isDeliveryPerson ? 'Successfully delivered by you' : 'Completed deliveries'}
              </p>
            </div>
          </div>

          {/* Filter Bar & Tabs */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs space-y-4">
            {/* Tabs */}
            <div className="flex items-center justify-between flex-wrap gap-3 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                {(['Active', 'Pending', 'Out for Delivery', 'Delivered', 'All'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab);
                      setPage(1);
                    }}
                    className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all whitespace-nowrap ${
                      activeTab === tab
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {tab === 'Active' ? 'Active Deliveries' : tab}
                  </button>
                ))}
              </div>

              {/* Date & Driver Filters */}
              <div className="flex items-center gap-2 text-xs flex-wrap">
                {!isDeliveryPerson && deliveryPersons.length > 0 && (
                  <select
                    value={selectedDriverFilter}
                    onChange={(e) => {
                      setSelectedDriverFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">All Drivers</option>
                    {deliveryPersons.map((m) => (
                      <option key={m.id || m.name} value={m.name}>
                        Driver: {m.name}
                      </option>
                    ))}
                  </select>
                )}

                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setPage(1);
                    }}
                    className="bg-transparent text-xs text-slate-700 focus:outline-none"
                    placeholder="From"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPage(1);
                    }}
                    className="bg-transparent text-xs text-slate-700 focus:outline-none"
                    placeholder="To"
                  />
                  {!(fromDate === getTodayDateString() && toDate === getTodayDateString()) && (
                    <button
                      type="button"
                      onClick={() => {
                        const today = getTodayDateString();
                        setFromDate(today);
                        setToDate(today);
                        setPage(1);
                      }}
                      className="px-2 py-0.5 text-[11px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                      title="Auto-fill today's date"
                    >
                      Today
                    </button>
                  )}
                  {(fromDate || toDate) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                        setPage(1);
                      }}
                      className="text-slate-400 hover:text-slate-600 ml-1"
                      title="Clear date filter (show all dates)"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Input */}
            <div className="relative w-full">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={
                  isDeliveryPerson
                    ? 'Search assigned orders by invoice #, customer, address, or phone...'
                    : 'Search by invoice #, customer name, recipient, phone, address, or postal code...'
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Out for Delivery Map Switcher & Banner for Admin */}
          {!isDeliveryPerson && activeTab === 'Out for Delivery' && (
            <div className="flex flex-wrap items-center justify-between gap-3 bg-indigo-50/70 border border-indigo-200/80 rounded-2xl p-4 shadow-2xs">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Live Driver Tracking & Out for Delivery Map</h4>
                  <p className="text-xs text-slate-500">
                    Track the real-time GPS locations of riders and orders currently in transit
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex bg-white rounded-xl p-1 border border-indigo-200 shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setOutForDeliveryView('list')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      outForDeliveryView === 'list'
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Orders List
                  </button>
                  <button
                    type="button"
                    onClick={() => setOutForDeliveryView('map')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      outForDeliveryView === 'map'
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Inline Map
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setMapInitialStatus('Out for Delivery');
                    setShowMapModal(true);
                  }}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  Full Map
                </button>
              </div>
            </div>
          )}

          {/* Conditional: If admin selected Inline Map View on Out for Delivery, render DeliveryLiveMap */}
          {!isDeliveryPerson && activeTab === 'Out for Delivery' && outForDeliveryView === 'map' ? (
            <DeliveryLiveMap
              initialStatus="Out for Delivery"
              initialDriver={selectedDriverFilter}
              initialFromDate={fromDate}
              initialToDate={toDate}
            />
          ) : (
            <>
              {/* Orders List / Cards */}
              {isLoading ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600 opacity-60" />
              <p className="text-sm font-medium">Loading deliveries...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-2xs">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3">
                <Truck className="w-8 h-8" />
              </div>
              <h3 className="text-base font-bold text-slate-900">
                {isDeliveryPerson ? 'No assigned deliveries found' : 'No deliveries found'}
              </h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                {isDeliveryPerson
                  ? 'You currently have no deliveries assigned to you in this status.'
                  : activeTab === 'Active'
                  ? 'There are currently no active deliveries waiting to be dispatched or delivered.'
                  : `No delivery orders matched the selected filter (${activeTab}).`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const isExpanded = !!expandedOrders[order.name];
                const phone = order.delivery_phone || order.customer_mobile;
                const recipient = order.delivery_recipient_name || order.customer_name || 'Customer';

                return (
                  <div
                    key={order.name}
                    className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs hover:shadow-md transition-all overflow-hidden"
                  >
                    {/* Order Header */}
                    <div className="px-5 py-3.5 bg-slate-50/60 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-sm text-slate-900">{order.name}</span>
                        {getStatusBadge(order.delivery_status)}
                        {!isDeliveryPerson ? (
                          getPaymentBadge(order.invoice_status, order.balance_amount)
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/60">
                            Assigned to You
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>{formatDate(order.invoice_date || order.creation)}</span>
                        {!isDeliveryPerson && (
                          <span className="font-semibold text-slate-900 ml-2">
                            Total: {formatMoney(order.net_total)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Order Details Body */}
                    <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
                      {/* Left: Customer & Address Info (7 cols) */}
                      <div className="md:col-span-7 space-y-3">
                        {/* Recipient / Customer */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                            <User className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-slate-900">{recipient}</p>
                            {order.customer_name && order.customer_name !== recipient && (
                              <p className="text-xs text-slate-500">Account: {order.customer_name}</p>
                            )}
                          </div>

                          {/* Quick Phone / WhatsApp Actions */}
                          {phone && (
                            <div className="flex items-center gap-1.5 ml-auto">
                              <a
                                href={`tel:${phone}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                              >
                                <Phone className="w-3 h-3 text-slate-600" />
                                <span>{phone}</span>
                              </a>
                              <a
                                href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors"
                                title="Open WhatsApp"
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Address */}
                        <div className="flex items-start gap-2.5 bg-slate-50 p-3 rounded-xl border border-slate-100">
                          <MapPin className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                          <div className="text-xs text-slate-700 flex-1 leading-relaxed">
                            <p className="font-semibold text-slate-900">
                              {order.delivery_address || 'No address specified'}
                            </p>
                            {order.delivery_postal_code && (
                              <p className="text-slate-500 font-mono text-[11px] mt-0.5">
                                Postal Code: {order.delivery_postal_code}
                              </p>
                            )}
                          </div>
                          {order.delivery_address && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                `${order.delivery_address} ${order.delivery_postal_code || ''}`
                              )}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5 shrink-0"
                            >
                              <span>Map</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>

                        {/* Delivery Driver & Notes */}
                        {(order.delivery_driver || order.delivery_notes) && (
                          <div className="text-xs text-slate-600 space-y-1">
                            {!isDeliveryPerson && order.delivery_driver && (
                              <p className="flex items-center gap-1.5">
                                <span className="font-semibold text-slate-800">Driver / Rider:</span>
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-medium text-[11px]">
                                  {order.delivery_driver}
                                </span>
                              </p>
                            )}
                            {order.delivery_notes && (
                              <div className="flex items-start gap-1.5 text-slate-500 text-[11px] bg-amber-50/50 p-2 rounded-lg border border-amber-100/60">
                                <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                <span>{order.delivery_notes}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: Items Preview & Action Buttons (5 cols) */}
                      <div className="md:col-span-5 flex flex-col justify-between h-full space-y-3 pt-2 md:pt-0 md:border-l md:border-slate-100 md:pl-5">
                        {/* Items Preview */}
                        <div>
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                            <span className="flex items-center gap-1.5">
                              <Package className="w-3.5 h-3.5" />
                              Items ({order.items?.length || 0})
                              {isDeliveryPerson && (
                                <span className="text-[10px] text-slate-400 font-normal">
                                  (tap item to check off)
                                </span>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleExpand(order.name)}
                              className="text-blue-600 hover:text-blue-800 text-[11px] flex items-center gap-0.5 font-medium"
                            >
                              <span>{isExpanded ? 'Hide' : 'View'} Items</span>
                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </button>
                          </div>

                          {isExpanded && order.items && order.items.length > 0 && (
                            <div className="space-y-1 bg-slate-50 p-2 rounded-xl border border-slate-100 max-h-48 overflow-y-auto mb-2 text-xs">
                              {order.items.map((it, idx) => {
                                const itemKey = `${order.name}-${idx}`;
                                const isChecked = !!checkedItems[itemKey];

                                return (
                                  <div
                                    key={idx}
                                    onClick={isDeliveryPerson ? () => toggleItemCheck(order.name, idx) : undefined}
                                    className={`flex items-center justify-between py-1.5 px-2 rounded-lg border-b border-slate-200/50 last:border-b-0 text-[11px] transition-all ${
                                      isDeliveryPerson ? 'cursor-pointer hover:bg-slate-100 select-none' : ''
                                    } ${isChecked ? 'bg-emerald-50/80 text-emerald-900' : ''}`}
                                  >
                                    <div className="flex items-center gap-2 truncate pr-2">
                                      {isDeliveryPerson && (
                                        <span
                                          className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                            isChecked
                                              ? 'bg-emerald-600 border-emerald-600 text-white'
                                              : 'border-slate-300 bg-white'
                                          }`}
                                        >
                                          {isChecked && <CheckCircle2 className="w-3 h-3" />}
                                        </span>
                                      )}
                                      <span
                                        className={`truncate ${
                                          isChecked ? 'line-through text-slate-400 font-normal' : 'text-slate-800 font-medium'
                                        }`}
                                      >
                                        <strong className="text-slate-900 font-bold mr-1">{it.quantity} ×</strong>
                                        {it.item_name}
                                        {it.variant_name ? ` (${it.variant_name})` : ''}
                                      </span>
                                    </div>

                                    {!isDeliveryPerson && it.amount !== undefined && (
                                      <span className="text-slate-600 font-mono shrink-0">
                                        {formatMoney(it.amount)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                          {order.delivery_status === 'Pending' && (
                            <button
                              type="button"
                              onClick={() => handleOpenStatusModal(order, 'Out for Delivery')}
                              className="flex-1 px-3 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5"
                            >
                              <Truck className="w-3.5 h-3.5" />
                              {isDeliveryPerson ? 'Start Delivery' : 'Dispatch'}
                            </button>
                          )}

                          {order.delivery_status === 'Out for Delivery' && (
                            <button
                              type="button"
                              onClick={() => handleOpenStatusModal(order, 'Delivered')}
                              className="flex-1 px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Mark Delivered
                            </button>
                          )}

                          {order.delivery_status === 'Pending' && (
                            <button
                              type="button"
                              onClick={() => handleOpenStatusModal(order, 'Delivered')}
                              className="px-3 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
                              title="Directly mark delivered"
                            >
                              Delivered
                            </button>
                          )}

                          {/* Print Delivery Slip */}
                          <button
                            type="button"
                            onClick={() => setReceiptInvoiceName(order.name)}
                            className="px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors flex items-center gap-1.5"
                            title="Print delivery slip (no prices)"
                          >
                            <Printer className="w-3.5 h-3.5 text-slate-500" />
                            <span>Slip</span>
                          </button>

                          {/* Update / Change Status Modal */}
                          <button
                            type="button"
                            onClick={() => handleOpenStatusModal(order)}
                            className="px-2.5 py-2 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                            title="Edit delivery status or notes"
                          >
                            {isDeliveryPerson ? 'Notes' : 'Edit'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 border border-slate-200/80 rounded-2xl text-xs">
                  <span className="text-slate-500">
                    Page <span className="font-bold text-slate-900">{page}</span> of{' '}
                    <span className="font-bold text-slate-900">{totalPages}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* UPDATE STATUS MODAL */}
      {updatingOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Truck className="w-5 h-5 text-white" />
                <h3 className="font-bold text-base">Update Delivery: {updatingOrder.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setUpdatingOrder(null)}
                className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveStatusUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Delivery Status *
                </label>
                <select
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="Pending">Pending (Not Dispatched)</option>
                  <option value="Out for Delivery">Out for Delivery (In Transit)</option>
                  <option value="Delivered">Delivered (Completed)</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {/* Delivery Assignment: Internal Team vs External (Managers / Admins only) */}
              {!isDeliveryPerson && (
                <div className="bg-slate-50/90 p-3.5 rounded-xl border border-slate-200/80 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Delivery Assignment
                    </label>
                    <span className="text-[11px] font-medium text-slate-500">
                      {isInternalTeam ? 'Delivery Person' : 'External Courier'}
                    </span>
                  </div>

                  {/* Segmented Radio / Switch */}
                  <div className="grid grid-cols-2 gap-1.5 bg-slate-200/70 p-1 rounded-xl text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setIsInternalTeam(true)}
                      className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg transition-all ${
                        isInternalTeam
                          ? 'bg-white text-blue-700 shadow-xs font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <User className="w-3.5 h-3.5" />
                      <span>Delivery Person</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsInternalTeam(false)}
                      className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg transition-all ${
                        !isInternalTeam
                          ? 'bg-white text-blue-700 shadow-xs font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Truck className="w-3.5 h-3.5" />
                      <span>External / Other</span>
                    </button>
                  </div>

                  {isInternalTeam ? (
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-slate-600">
                        Select Delivery Person
                      </label>
                      <select
                        value={selectedTeamMember}
                        onChange={(e) => setSelectedTeamMember(e.target.value)}
                        className="w-full px-3.5 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
                      >
                        <option value="">-- Select Delivery Person --</option>
                        {deliveryPersons.map((member) => (
                          <option key={member.id || member.name} value={member.name}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                      {deliveryPersons.length === 0 && (
                        <p className="text-[11px] text-amber-600 mt-1">
                          No team members with &quot;Delivery Person&quot; role found. You can add one in Team Users or switch to External.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-slate-600">
                        Courier / Driver Details
                      </label>
                      <input
                        type="text"
                        value={externalDriverDetails}
                        onChange={(e) => setExternalDriverDetails(e.target.value)}
                        placeholder="e.g. Uber Eats, Deliveroo, DPD, John Doe (Courier)..."
                        className="w-full px-3.5 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-slate-400 text-slate-800"
                      />
                      <p className="text-[11px] text-slate-400">
                        Write courier company name, driver name, vehicle reg, or tracking details.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Add Delivery Note (optional)
                </label>
                <textarea
                  rows={2}
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="e.g. Handed over to recipient, signed delivery receipt..."
                  className="w-full px-3.5 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-slate-400"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setUpdatingOrder(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateStatusMutation.isPending}
                  className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-xs transition-all disabled:opacity-50"
                >
                  {updateStatusMutation.isPending ? 'Saving...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RECEIPT / DELIVERY SLIP MODAL */}
      {receiptInvoiceName && (
        <Receipt
          invoiceName={receiptInvoiceName}
          initialMode="delivery_slip"
          onClose={() => setReceiptInvoiceName(null)}
        />
      )}

      {/* ADMIN LIVE DRIVER TRACKING MAP MODAL */}
      <DeliveryMapModal
        open={showMapModal}
        onClose={() => setShowMapModal(false)}
        initialStatus={mapInitialStatus}
        initialDriver={selectedDriverFilter}
        initialFromDate={fromDate}
        initialToDate={toDate}
      />
    </div>
  );
}
